import { GridSizeChangedEvent } from 'ag-grid-community'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { useAuth } from 'services/auth'
import { Run, WellConfiguration, WellCount, WellMetric } from '../../utils/api'
import AgGridRunDetailsTable from '../shared/AgGridRunDetailsTable'
import { getSortByWellColumnDefs } from './metadata'
import { useUpdatedSortMetricsAccess } from './useUpdatedSortMetricsAccess'
import useResetGridColumnState from './useResetGridColumnState'

interface IWellResultsGridProps {
  run: Run
}

type Well = WellConfiguration & WellCount & WellMetric

const WellResultsGrid = ({ run }: IWellResultsGridProps): ReactElement => {
  const [wells, setWells] = useState<Well[]>([])
  const { isInternalUser } = useAuth()
  const updatedMetricsEnabled = useUpdatedSortMetricsAccess()

  // Detect if run has new data format (has true_positive_count field)
  const hasNewDataFormat = useMemo(() => {
    const firstWellCount = run.well_counts?.[0]
    return firstWellCount?.true_positive_count !== undefined
  }, [run.well_counts])

  useEffect(() => {
    const configurations = run.well_sorting_configurations ?? []
    const counts = run.well_counts ?? []
    const metrics = run.well_metrics ?? []

    const countByWellId = counts.reduce((obj: Record<number, WellCount>, item: WellCount) => {
      // eslint-disable-next-line no-param-reassign
      obj[item.well] = item
      return obj
    }, {})

    const metricsByWellId = metrics.reduce((obj: Record<number, WellMetric>, item: WellMetric) => {
      // eslint-disable-next-line no-param-reassign
      obj[item.well] = item
      return obj
    }, {})

    const data = configurations.map((config: WellConfiguration) => ({
      ...config,
      ...(countByWellId[config.well] ?? {}),
      ...(metricsByWellId[config.well] ?? {}),
    }))
    setWells(data)
  }, [run])

  const columnDefs = useMemo(
    () =>
      getSortByWellColumnDefs({
        isInternalUser,
        updatedMetricsEnabled,
        hasNewDataFormat,
      }),
    [isInternalUser, updatedMetricsEnabled, hasNewDataFormat]
  )

  const onGridReady = useResetGridColumnState(columnDefs)

  const onGridSizeChange = (params: GridSizeChangedEvent) => {
    params.api.sizeColumnsToFit()
  }

  return (
    <AgGridRunDetailsTable
      customClass="context-menu-ag-grid"
      rowData={wells
        .filter((wellMetric) => wellMetric.well > 0)
        .sort((well1, well2) => well1.well - well2.well)}
      columnDefs={columnDefs}
      onGridReady={onGridReady}
      onGridSizeChanged={onGridSizeChange}
      testId="well-result-grid"
      headerHeight={32}
      suppressMovableColumns
      suppressColumnMoveAnimation
    />
  )
}

export default WellResultsGrid
